(function () {
  const HOVER_PREFETCH_DELAY_MS = 50;
  const HOVER_PRERENDER_DELAY_MS = 180;
  const MAX_SPECULATION_PREFETCHES = 24;
  const MAX_SPECULATION_PRERENDERS = 8;
  const prefetchedUrls = new Set();
  const prerenderedUrls = new Set();
  const hoverTimers = new WeakMap();
  const hoverPrerenderTimers = new WeakMap();
  const samePageUrl = new URL(window.location.href);
  const skipSchemePattern = /^(?:mailto:|tel:|sms:|javascript:|data:)/i;
  const skipPathPattern = /^\/(?:cart|checkout|challenge|account\/logout)(?:\/|$)/i;
  const skipFilePattern = /\.(?:pdf|zip|rar|7z|jpe?g|png|webp|gif|svg|avif|mp4|mov|webm|mp3|wav|csv|xml|json|txt)$/i;
  const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
  const prefetchProbe = document.createElement('link');
  const supportsPrefetch =
    !!prefetchProbe.relList && typeof prefetchProbe.relList.supports === 'function' && prefetchProbe.relList.supports('prefetch');
  const supportsSpeculationRules =
    !!window.HTMLScriptElement &&
    typeof HTMLScriptElement.supports === 'function' &&
    HTMLScriptElement.supports('speculationrules');
  const speculationState = {
    prefetch: [],
    prerender: [],
  };
  let speculationScript = null;

  if (connection && (connection.saveData || /(?:^|slow-)2g/.test(connection.effectiveType || ''))) {
    return;
  }

  function ensureSpeculationScript() {
    if (!supportsSpeculationRules) {
      return null;
    }

    if (!speculationScript) {
      speculationScript = document.createElement('script');
      speculationScript.type = 'speculationrules';
      speculationScript.dataset.instantNavigation = 'true';
      document.head.appendChild(speculationScript);
    }

    return speculationScript;
  }

  function syncSpeculationScript() {
    const script = ensureSpeculationScript();
    if (!script) {
      return;
    }

    const rules = {};

    if (speculationState.prefetch.length) {
      rules.prefetch = [
        {
          source: 'list',
          urls: speculationState.prefetch,
        },
      ];
    }

    if (speculationState.prerender.length) {
      rules.prerender = [
        {
          source: 'list',
          urls: speculationState.prerender,
        },
      ];
    }

    script.textContent = JSON.stringify(rules);
  }

  function pushUnique(queue, href, maxLength) {
    const existingIndex = queue.indexOf(href);
    if (existingIndex !== -1) {
      queue.splice(existingIndex, 1);
    }

    queue.push(href);

    while (queue.length > maxLength) {
      queue.shift();
    }
  }

  function queueSpeculation(url, mode) {
    if (!supportsSpeculationRules) {
      return false;
    }

    const href = url.href;

    if (mode === 'prerender') {
      if (prerenderedUrls.has(href)) {
        return true;
      }

      prerenderedUrls.add(href);
      prefetchedUrls.add(href);
      speculationState.prefetch = speculationState.prefetch.filter(function (item) {
        return item !== href;
      });
      pushUnique(speculationState.prerender, href, MAX_SPECULATION_PRERENDERS);
      syncSpeculationScript();
      return true;
    }

    if (prefetchedUrls.has(href) || prerenderedUrls.has(href)) {
      return true;
    }

    prefetchedUrls.add(href);
    pushUnique(speculationState.prefetch, href, MAX_SPECULATION_PREFETCHES);
    syncSpeculationScript();
    return true;
  }

  function clearHoverTimer(link) {
    const prefetchTimerId = hoverTimers.get(link);
    if (prefetchTimerId) {
      window.clearTimeout(prefetchTimerId);
      hoverTimers.delete(link);
    }

    const prerenderTimerId = hoverPrerenderTimers.get(link);
    if (prerenderTimerId) {
      window.clearTimeout(prerenderTimerId);
      hoverPrerenderTimers.delete(link);
    }
  }

  function getEligibleLink(target) {
    if (!target || !target.closest) {
      return null;
    }

    const link = target.closest('a[href]');
    if (!link) {
      return null;
    }

    if (link.dataset.noPrefetch !== undefined || link.dataset.noInstant !== undefined) {
      return null;
    }

    if (link.hasAttribute('download')) {
      return null;
    }

    if (link.target && link.target.toLowerCase() !== '_self') {
      return null;
    }

    const rawHref = link.getAttribute('href');
    if (!rawHref || rawHref.charAt(0) === '#' || skipSchemePattern.test(rawHref)) {
      return null;
    }

    let url;

    try {
      url = new URL(link.href, samePageUrl.href);
    } catch (error) {
      return null;
    }

    if (url.origin !== samePageUrl.origin || !/^https?:$/.test(url.protocol)) {
      return null;
    }

    if (url.pathname === samePageUrl.pathname && url.search === samePageUrl.search) {
      return null;
    }

    if (skipPathPattern.test(url.pathname) || skipFilePattern.test(url.pathname)) {
      return null;
    }

    return { link, url };
  }

  function prefetchUrl(url) {
    const href = url.href;

    if (queueSpeculation(url, 'prefetch')) {
      return;
    }

    if (prefetchedUrls.has(href)) {
      return;
    }

    prefetchedUrls.add(href);

    if (supportsPrefetch) {
      const prefetchLink = document.createElement('link');
      prefetchLink.rel = 'prefetch';
      prefetchLink.href = href;
      prefetchLink.setAttribute('fetchpriority', 'low');
      prefetchLink.dataset.instantPrefetch = 'true';
      prefetchLink.addEventListener(
        'error',
        function () {
          prefetchedUrls.delete(href);
        },
        { once: true }
      );
      document.head.appendChild(prefetchLink);
      return;
    }

    fetch(href, { credentials: 'same-origin' }).catch(function () {
      prefetchedUrls.delete(href);
    });
  }

  function prerenderUrl(url) {
    if (queueSpeculation(url, 'prerender')) {
      return;
    }

    prefetchUrl(url);
  }

  document.addEventListener(
    'pointerover',
    function (event) {
      if (event.pointerType && event.pointerType !== 'mouse') {
        return;
      }

      const match = getEligibleLink(event.target);
      if (!match) {
        return;
      }

      clearHoverTimer(match.link);

      const prefetchTimerId = window.setTimeout(function () {
        hoverTimers.delete(match.link);
        prefetchUrl(match.url);
      }, HOVER_PREFETCH_DELAY_MS);

      hoverTimers.set(match.link, prefetchTimerId);

      const prerenderTimerId = window.setTimeout(function () {
        hoverPrerenderTimers.delete(match.link);
        prerenderUrl(match.url);
      }, HOVER_PRERENDER_DELAY_MS);

      hoverPrerenderTimers.set(match.link, prerenderTimerId);
    },
    true
  );

  document.addEventListener(
    'pointerout',
    function (event) {
      const match = getEligibleLink(event.target);
      if (!match) {
        return;
      }

      if (event.relatedTarget && match.link.contains(event.relatedTarget)) {
        return;
      }

      clearHoverTimer(match.link);
    },
    true
  );

  document.addEventListener(
    'pointerdown',
    function (event) {
      const match = getEligibleLink(event.target);
      if (!match) {
        return;
      }

      prerenderUrl(match.url);
    },
    true
  );

  document.addEventListener(
    'focusin',
    function (event) {
      const match = getEligibleLink(event.target);
      if (!match) {
        return;
      }

      prefetchUrl(match.url);
    },
    true
  );

  document.addEventListener(
    'touchstart',
    function (event) {
      const match = getEligibleLink(event.target);
      if (!match) {
        return;
      }

      prerenderUrl(match.url);
    },
    { capture: true, passive: true }
  );
})();
if (!customElements.get('product-info')) {
  customElements.define(
    'product-info',
    class ProductInfo extends HTMLElement {
      quantityInput = undefined;
      quantityForm = undefined;
      onVariantChangeUnsubscriber = undefined;
      cartUpdateUnsubscriber = undefined;
      abortController = undefined;
      pendingRequestUrl = null;
      preProcessHtmlCallbacks = [];
      postProcessHtmlCallbacks = [];

      constructor() {
        super();

        this.quantityInput = this.querySelector('.quantity__input');
      }

      connectedCallback() {
        this.initializeProductSwapUtility();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );

        this.initQuantityHandlers();
        this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
      }

      addPreProcessCallback(callback) {
        this.preProcessHtmlCallbacks.push(callback);
      }

      initQuantityHandlers() {
        if (!this.quantityInput) return;

        this.quantityForm = this.querySelector('.product-form__quantity');
        if (!this.quantityForm) return;

        this.setQuantityBoundries();
        if (!this.dataset.originalSection) {
          this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
        }
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
        this.cartUpdateUnsubscriber?.();
      }

      initializeProductSwapUtility() {
        this.preProcessHtmlCallbacks.push((html) =>
          html.querySelectorAll('.scroll-trigger').forEach((element) => element.classList.add('scroll-trigger--cancel'))
        );
        this.postProcessHtmlCallbacks.push((newNode) => {
          window?.Shopify?.PaymentButton?.init();
          window?.ProductModel?.loadShopifyXR();
        });
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
        if (!this.contains(event.target)) return;

        this.resetProductFormState();

        const productUrl = target.dataset.productUrl || this.pendingRequestUrl || this.dataset.url;
        this.pendingRequestUrl = productUrl;
        const shouldSwapProduct = this.dataset.url !== productUrl;
        const shouldFetchFullPage = this.dataset.updateUrl === 'true' && shouldSwapProduct;

        this.renderProductInfo({
          requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues, shouldFetchFullPage),
          targetId: target.id,
          callback: shouldSwapProduct
            ? this.handleSwapProduct(productUrl, shouldFetchFullPage)
            : this.handleUpdateProductInfo(productUrl),
        });
      }

      resetProductFormState() {
        const productForm = this.productForm;
        productForm?.toggleSubmitButton(true);
        productForm?.handleErrorMessage();
      }

      handleSwapProduct(productUrl, updateFullPage) {
        return (html) => {
          this.productModal?.remove();

          const selector = updateFullPage ? "product-info[id^='MainProduct']" : 'product-info';
          const variant = this.getSelectedVariant(html.querySelector(selector));
          this.updateURL(productUrl, variant?.id);

          if (updateFullPage) {
            document.querySelector('head title').innerHTML = html.querySelector('head title').innerHTML;

            HTMLUpdateUtility.viewTransition(
              document.querySelector('main'),
              html.querySelector('main'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          } else {
            HTMLUpdateUtility.viewTransition(
              this,
              html.querySelector('product-info'),
              this.preProcessHtmlCallbacks,
              this.postProcessHtmlCallbacks
            );
          }
        };
      }

      renderProductInfo({ requestUrl, targetId, callback }) {
        this.abortController?.abort();
        this.abortController = new AbortController();

        fetch(requestUrl, { signal: this.abortController.signal })
          .then((response) => response.text())
          .then((responseText) => {
            this.pendingRequestUrl = null;
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            callback(html);
          })
          .then(() => {
            // set focus to last clicked option value
            document.querySelector(`#${targetId}`)?.focus();
          })
          .catch((error) => {
            if (error.name === 'AbortError') {
              console.log('Fetch aborted by user');
            } else {
              console.error(error);
            }
          });
      }

      getSelectedVariant(productInfoNode) {
        const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
        return !!selectedVariant ? JSON.parse(selectedVariant) : null;
      }

      buildRequestUrlWithParams(url, optionValues, shouldFetchFullPage = false) {
        const params = [];

        !shouldFetchFullPage && params.push(`section_id=${this.sectionId}`);

        if (optionValues.length) {
          params.push(`option_values=${optionValues.join(',')}`);
        }

        return `${url}?${params.join('&')}`;
      }

      updateOptionValues(html) {
        const variantSelects = html.querySelector('variant-selects');
        if (variantSelects) {
          HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects, this.preProcessHtmlCallbacks);
        }
      }

      handleUpdateProductInfo(productUrl) {
        return (html) => {
          const variant = this.getSelectedVariant(html);

          this.pickupAvailability?.update(variant);
          this.updateOptionValues(html);
          this.updateURL(productUrl, variant?.id);
          this.updateVariantInputs(variant?.id);

          if (!variant) {
            this.setUnavailable();
            return;
          }

          this.updateMedia(html, variant?.featured_media?.id);

          const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
            const source = html.getElementById(`${id}-${this.sectionId}`);
            const destination = this.querySelector(`#${id}-${this.dataset.section}`);
            if (source && destination) {
              destination.innerHTML = source.innerHTML;
              destination.classList.toggle('hidden', shouldHide(source));
            }
          };

          updateSourceFromDestination('price');
          updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
          updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
          updateSourceFromDestination('Volume');
          updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));

          this.updateQuantityRules(this.sectionId, html);
          this.querySelector(`#Quantity-Rules-${this.dataset.section}`)?.classList.remove('hidden');
          this.querySelector(`#Volume-Note-${this.dataset.section}`)?.classList.remove('hidden');

          this.productForm?.toggleSubmitButton(
            html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
            window.variantStrings.soldOut
          );

          publish(PUB_SUB_EVENTS.variantChange, {
            data: {
              sectionId: this.sectionId,
              html,
              variant,
            },
          });
        };
      }

      updateVariantInputs(variantId) {
        this.querySelectorAll(
          `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
        ).forEach((productForm) => {
          const input = productForm.querySelector('input[name="id"]');
          input.value = variantId ?? '';
          input.dispatchEvent(new Event('change', { bubbles: true }));
        });
      }

      updateURL(url, variantId) {
        this.querySelector('share-button')?.updateUrl(
          `${window.shopUrl}${url}${variantId ? `?variant=${variantId}` : ''}`
        );

        if (this.dataset.updateUrl === 'false') return;
        window.history.replaceState({}, '', `${url}${variantId ? `?variant=${variantId}` : ''}`);
      }

      setUnavailable() {
        this.productForm?.toggleSubmitButton(true, window.variantStrings.unavailable);

        const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
          .map((id) => `#${id}-${this.dataset.section}`)
          .join(', ');
        document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
      }

      updateMedia(html, variantFeaturedMediaId) {
        if (!variantFeaturedMediaId) return;

        const mediaGallerySource = this.querySelector('media-gallery ul');
        const mediaGalleryDestination = html.querySelector(`media-gallery ul`);

        const refreshSourceData = () => {
          if (this.hasAttribute('data-zoom-on-hover')) enableZoomOnHover(2);
          const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
          const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
          const sourceMap = new Map(
            mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }])
          );
          return [mediaGallerySourceItems, sourceSet, sourceMap];
        };

        if (mediaGallerySource && mediaGalleryDestination) {
          let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
          const mediaGalleryDestinationItems = Array.from(
            mediaGalleryDestination.querySelectorAll('li[data-media-id]')
          );
          const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
          let shouldRefresh = false;

          // add items from new data not present in DOM
          for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
            if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
              mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
              shouldRefresh = true;
            }
          }

          // remove items from DOM not present in new data
          for (let i = 0; i < mediaGallerySourceItems.length; i++) {
            if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
              mediaGallerySourceItems[i].remove();
              shouldRefresh = true;
            }
          }

          // refresh
          if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

          // if media galleries don't match, sort to match new data order
          mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
            const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

            if (sourceData && sourceData.index !== destinationIndex) {
              mediaGallerySource.insertBefore(
                sourceData.item,
                mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
              );

              // refresh source now that it has been modified
              [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
            }
          });
        }

        // set featured media as active in the media gallery
        this.querySelector(`media-gallery`)?.setActiveMedia?.(
          `${this.dataset.section}-${variantFeaturedMediaId}`,
          true
        );

        // update media modal
        const modalContent = this.productModal?.querySelector(`.product-media-modal__content`);
        const newModalContent = html.querySelector(`product-modal .product-media-modal__content`);
        if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
      }

      setQuantityBoundries() {
        const data = {
          cartQuantity: this.quantityInput.dataset.cartQuantity ? parseInt(this.quantityInput.dataset.cartQuantity) : 0,
          min: this.quantityInput.dataset.min ? parseInt(this.quantityInput.dataset.min) : 1,
          max: this.quantityInput.dataset.max ? parseInt(this.quantityInput.dataset.max) : null,
          step: this.quantityInput.step ? parseInt(this.quantityInput.step) : 1,
        };

        let min = data.min;
        const max = data.max === null ? data.max : data.max - data.cartQuantity;
        if (max !== null) min = Math.min(min, max);
        if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

        this.quantityInput.min = min;

        if (max) {
          this.quantityInput.max = max;
        } else {
          this.quantityInput.removeAttribute('max');
        }
        this.quantityInput.value = min;

        publish(PUB_SUB_EVENTS.quantityUpdate, undefined);
      }

      fetchQuantityRules() {
        const currentVariantId = this.productForm?.variantIdInput?.value;
        if (!currentVariantId) return;

        this.querySelector('.quantity__rules-cart .loading__spinner').classList.remove('hidden');
        fetch(`${this.dataset.url}?variant=${currentVariantId}&section_id=${this.dataset.section}`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            this.updateQuantityRules(this.dataset.section, html);
          })
          .catch((e) => console.error(e))
          .finally(() => this.querySelector('.quantity__rules-cart .loading__spinner').classList.add('hidden'));
      }

      updateQuantityRules(sectionId, html) {
        if (!this.quantityInput) return;
        this.setQuantityBoundries();

        const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
        const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
        for (let selector of selectors) {
          const current = this.quantityForm.querySelector(selector);
          const updated = quantityFormUpdated.querySelector(selector);
          if (!current || !updated) continue;
          if (selector === '.quantity__input') {
            const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
            for (let attribute of attributes) {
              const valueUpdated = updated.getAttribute(attribute);
              if (valueUpdated !== null) {
                current.setAttribute(attribute, valueUpdated);
              } else {
                current.removeAttribute(attribute);
              }
            }
          } else {
            current.innerHTML = updated.innerHTML;
          }
        }
      }

      get productForm() {
        return this.querySelector(`product-form`);
      }

      get productModal() {
        return document.querySelector(`#ProductModal-${this.dataset.section}`);
      }

      get pickupAvailability() {
        return this.querySelector(`pickup-availability`);
      }

      get variantSelectors() {
        return this.querySelector('variant-selects');
      }

      get relatedProducts() {
        const relatedProductsSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'related-products'
        );
        return document.querySelector(`product-recommendations[data-section-id^="${relatedProductsSectionId}"]`);
      }

      get quickOrderList() {
        const quickOrderListSectionId = SectionId.getIdForSection(
          SectionId.parseId(this.sectionId),
          'quick_order_list'
        );
        return document.querySelector(`quick-order-list[data-id^="${quickOrderListSectionId}"]`);
      }

      get sectionId() {
        return this.dataset.originalSection || this.dataset.section;
      }
    }
  );
}

(() => {
  const initPpv2 = (root) => {
    if (!root || root.dataset.ppv2Init === 'true') return;
    root.dataset.ppv2Init = 'true';

    const scrollButtons = root.querySelectorAll('[data-scroll-target]');
    scrollButtons.forEach((btn) => {
      btn.addEventListener('click', () => {
        const target = btn.getAttribute('data-scroll-target');
        const dir = parseInt(btn.getAttribute('data-scroll-direction'), 10) || 1;
        const container = root.querySelector(`[data-scroll-container=\"${target}\"]`);
        if (!container) return;
        const step = btn.getAttribute('data-scroll-step');
        const count = Math.max(1, parseInt(btn.getAttribute('data-scroll-count'), 10) || 1);
        let amount = Math.round(container.clientWidth * 0.7) || 240;
        if (step === 'full') {
          amount = container.clientWidth;
        }
        if (step === 'cards') {
          const card = container.firstElementChild;
          const cardWidth = card ? card.getBoundingClientRect().width : container.clientWidth / count;
          const styles = getComputedStyle(container);
          const gapValue = styles.gap || styles.columnGap || '0';
          const gap = parseFloat(gapValue) || 0;
          amount = cardWidth * count + gap * (count - 1);
        }
        container.scrollBy({ left: amount * dir, behavior: 'smooth' });
      });
    });

    const filterWrap = root.querySelector('[data-ppv2-filters]');
    if (filterWrap) {
      const selects = Array.from(filterWrap.querySelectorAll('select[data-ppv2-filter]'));
      const resetBtn = filterWrap.querySelector('[data-ppv2-filter-reset]');
      const tiles = Array.from(root.querySelectorAll('.ppv2-topstripTile'));
      const emptyState = root.querySelector('[data-ppv2-topstrip-empty]');

      const normalize = (value) => (value || '').toString().trim().toLowerCase();

      const applyFilters = () => {
        const filters = selects.map((select) => ({
          key: select.dataset.ppv2Filter,
          value: normalize(select.value),
        }));

        let shown = 0;
        tiles.forEach((tile) => {
          let matches = true;
          for (let i = 0; i < filters.length; i += 1) {
            const filter = filters[i];
            if (!filter.value || filter.value === 'any') continue;
            const tileValue = normalize(tile.dataset[filter.key]);
            if (!tileValue || tileValue !== filter.value) {
              matches = false;
              break;
            }
          }
          tile.style.display = matches ? '' : 'none';
          if (matches) shown += 1;
        });

        if (emptyState) {
          emptyState.classList.toggle('is-visible', shown === 0);
        }
      };

      selects.forEach((select) => {
        select.addEventListener('change', applyFilters);
      });

      if (resetBtn) {
        resetBtn.addEventListener('click', () => {
          selects.forEach((select) => {
            select.value = 'any';
          });
          applyFilters();
        });
      }

      applyFilters();
    }

    const mainImg = root.querySelector('[data-ppv2-main]');
    const thumbs = Array.from(root.querySelectorAll('[data-ppv2-thumb]'));
    const dots = Array.from(root.querySelectorAll('[data-ppv2-dot]'));
    let activeIndex = 0;

    if (thumbs.length) {
      const initialIndex = thumbs.findIndex((thumb) => thumb.classList.contains('is-active'));
      if (initialIndex >= 0) activeIndex = initialIndex;
    }

    const setActive = (index) => {
      if (!mainImg || thumbs.length === 0) return;
      const clamped = Math.max(0, Math.min(index, thumbs.length - 1));
      activeIndex = clamped;
      const activeThumb = thumbs[activeIndex];
      const full = activeThumb.getAttribute('data-full');
      if (full) {
        mainImg.setAttribute('src', full);
      }
      thumbs.forEach((thumb, i) => {
        thumb.classList.toggle('is-active', i === activeIndex);
      });
      dots.forEach((dot, i) => {
        dot.classList.toggle('is-active', i === activeIndex);
      });
    };

    if (thumbs.length) {
      setActive(activeIndex);
      thumbs.forEach((thumb) => {
        thumb.addEventListener('click', () => {
          const index = parseInt(thumb.getAttribute('data-index'), 10) || 0;
          setActive(index);
        });
      });
    }

    dots.forEach((dot) => {
      dot.addEventListener('click', () => {
        const index = parseInt(dot.getAttribute('data-index'), 10) || 0;
        setActive(index);
      });
    });

    const prevBtn = root.querySelector('[data-ppv2-media-prev]');
    const nextBtn = root.querySelector('[data-ppv2-media-next]');
    if (prevBtn) {
      prevBtn.addEventListener('click', () => setActive(activeIndex - 1));
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => setActive(activeIndex + 1));
    }

    const tabs = Array.from(root.querySelectorAll('.ppv2-tab'));
    const panels = Array.from(root.querySelectorAll('.ppv2-tabPanel'));
    tabs.forEach((tab) => {
      tab.addEventListener('click', () => {
        const target = tab.getAttribute('data-tab-target');
        tabs.forEach((t) => {
          t.classList.toggle('is-active', t === tab);
          t.setAttribute('aria-selected', t === tab ? 'true' : 'false');
        });
        panels.forEach((panel) => {
          panel.classList.toggle('is-active', panel.getAttribute('data-tab-panel') === target);
        });
      });
    });

    const accordionHeaders = Array.from(root.querySelectorAll('.ppv2-accordionHeader'));
    accordionHeaders.forEach((header) => {
      header.addEventListener('click', () => {
        const isExpanded = header.getAttribute('aria-expanded') === 'true';
        accordionHeaders.forEach((item) => {
          item.setAttribute('aria-expanded', 'false');
          const body = item.parentElement.querySelector('.ppv2-accordionBody');
          if (body) body.classList.remove('is-open');
        });
        if (!isExpanded) {
          header.setAttribute('aria-expanded', 'true');
          const body = header.parentElement.querySelector('.ppv2-accordionBody');
          if (body) body.classList.add('is-open');
        }
      });
    });

    const quickToggle = root.querySelector('.ppv2-quickSpecsToggle');
    const quickBody = root.querySelector('.ppv2-quickSpecsBody');
    if (quickToggle && quickBody) {
      quickToggle.addEventListener('click', () => {
        const isOpen = quickBody.classList.contains('is-open');
        quickBody.classList.toggle('is-open', !isOpen);
        quickToggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      });
    }

    const modal = root.querySelector('[data-ppv2-modal]');
    if (modal && modal.dataset.ppv2ModalInit !== 'true') {
      modal.dataset.ppv2ModalInit = 'true';
      const mediaMain = root.querySelector('.ppv2-mediaMain');
      const modalContent = modal.querySelector('.ppv2-imageModal__content');
      const modalImg = modal.querySelector('[data-ppv2-modal-img]');
      const modalCaption = modal.querySelector('[data-ppv2-modal-caption]');
      const closeEls = Array.from(modal.querySelectorAll('[data-ppv2-modal-close]'));
      const dragHandle = modal.querySelector('[data-ppv2-modal-drag]');
      const titleEl = root.querySelector('.ppv2-sidebarTitle');
      let lastFocusedElement = null;
      let dragState = null;

      const getTitle = () => (titleEl ? titleEl.textContent.trim() : '');
      const setModalOffset = (x, y) => {
        if (!modalContent) return;
        modalContent.style.setProperty('--ppv2-modal-offset-x', `${x}px`);
        modalContent.style.setProperty('--ppv2-modal-offset-y', `${y}px`);
      };
      const resetModalOffset = () => {
        setModalOffset(0, 0);
      };
      const closeAnyOpenModal = () => {
        const openModalEl = document.querySelector('.ppv2-imageModal.is-open');
        if (!openModalEl) return;
        openModalEl.classList.remove('is-open');
        openModalEl.setAttribute('aria-hidden', 'true');
        const openModalContent = openModalEl.querySelector('.ppv2-imageModal__content');
        if (openModalContent) {
          openModalContent.classList.remove('is-dragging');
          openModalContent.style.setProperty('--ppv2-modal-offset-x', '0px');
          openModalContent.style.setProperty('--ppv2-modal-offset-y', '0px');
        }
        document.body.classList.remove('ppv2-modal-open');
      };

      const openModal = () => {
        if (!modalImg || !mainImg || !modalContent) return;
        const src = mainImg.currentSrc || mainImg.getAttribute('src');
        if (!src) return;
        lastFocusedElement = document.activeElement;
        modalImg.setAttribute('src', src);
        modalImg.setAttribute('alt', mainImg.getAttribute('alt') || getTitle());
        if (modalCaption) {
          modalCaption.textContent = getTitle() || mainImg.getAttribute('alt') || '';
        }
        modalContent.classList.remove('is-dragging');
        resetModalOffset();
        modal.classList.add('is-open');
        modal.setAttribute('aria-hidden', 'false');
        document.body.classList.add('ppv2-modal-open');
        modalContent.focus();
      };

      const closeModal = () => {
        if (modalContent) {
          modalContent.classList.remove('is-dragging');
        }
        dragState = null;
        resetModalOffset();
        modal.classList.remove('is-open');
        modal.setAttribute('aria-hidden', 'true');
        document.body.classList.remove('ppv2-modal-open');
        if (lastFocusedElement && typeof lastFocusedElement.focus === 'function') {
          lastFocusedElement.focus();
        }
      };

      const updateDragPosition = (clientX, clientY) => {
        if (!dragState || !modalContent) return;
        const rect = modalContent.getBoundingClientRect();
        const maxX = Math.max(0, (window.innerWidth - rect.width) / 2 - 12);
        const maxY = Math.max(0, (window.innerHeight - rect.height) / 2 - 12);
        const nextX = dragState.originX + (clientX - dragState.startX);
        const nextY = dragState.originY + (clientY - dragState.startY);
        const clampedX = Math.max(-maxX, Math.min(maxX, nextX));
        const clampedY = Math.max(-maxY, Math.min(maxY, nextY));
        setModalOffset(clampedX, clampedY);
      };

      const onDragMove = (event) => {
        updateDragPosition(event.clientX, event.clientY);
      };

      const onDragEnd = () => {
        if (modalContent) {
          modalContent.classList.remove('is-dragging');
        }
        dragState = null;
        document.removeEventListener('pointermove', onDragMove);
        document.removeEventListener('pointerup', onDragEnd);
        document.removeEventListener('pointercancel', onDragEnd);
      };

      if (mediaMain) {
        mediaMain.addEventListener('click', (event) => {
          if (event.target.closest('[data-ppv2-media-prev], [data-ppv2-media-next]')) return;
          openModal();
        });
      }

      closeEls.forEach((el) => el.addEventListener('click', closeModal));

      modal.addEventListener('click', (event) => {
        if (!modal.classList.contains('is-open')) return;
        if (event.target.closest('.ppv2-imageModal__content')) return;
        closeModal();
      });

      if (dragHandle && modalContent) {
        dragHandle.addEventListener('pointerdown', (event) => {
          if (event.target.closest('[data-ppv2-modal-close]')) return;
          if (event.button !== 0) return;
          event.preventDefault();
          const offsetX = parseFloat(modalContent.style.getPropertyValue('--ppv2-modal-offset-x')) || 0;
          const offsetY = parseFloat(modalContent.style.getPropertyValue('--ppv2-modal-offset-y')) || 0;
          dragState = {
            startX: event.clientX,
            startY: event.clientY,
            originX: offsetX,
            originY: offsetY,
          };
          modalContent.classList.add('is-dragging');
          document.addEventListener('pointermove', onDragMove);
          document.addEventListener('pointerup', onDragEnd);
          document.addEventListener('pointercancel', onDragEnd);
        });
      }

      if (!document.body.dataset.ppv2ModalListener) {
        document.body.dataset.ppv2ModalListener = 'true';
        document.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape') return;
          closeAnyOpenModal();
        });
      }
    }
  };

  const initTopstripV2 = (wrap) => {
    if (!wrap || wrap.dataset.topstripV2Init === 'true') return;
    wrap.dataset.topstripV2Init = 'true';

    const track = wrap.querySelector('.topstripV2__track');
    if (!track) return;

    const prevBtn = wrap.querySelector('[data-topstripv2-nav="prev"]');
    const nextBtn = wrap.querySelector('[data-topstripv2-nav="next"]');

    const scrollByCards = (direction) => {
      const card = track.querySelector('.topstripV2__card');
      const cardWidth = card ? card.getBoundingClientRect().width : 220;
      const gap = parseFloat(getComputedStyle(track).gap) || 16;
      const amount = (cardWidth + gap) * 3 * direction;
      track.scrollBy({ left: amount, behavior: 'smooth' });
    };

    if (prevBtn) {
      prevBtn.addEventListener('click', () => scrollByCards(-1));
    }
    if (nextBtn) {
      nextBtn.addEventListener('click', () => scrollByCards(1));
    }

    const filterForm = wrap.querySelector('[data-topstripv2-filters]');
    const filterToggle = wrap.querySelector('[data-topstripv2-toggle]');
    const clearBtn = wrap.querySelector('[data-topstripv2-clear]');
    const emptyState = wrap.querySelector('[data-topstripv2-empty]');
    const cards = Array.from(wrap.querySelectorAll('.topstripV2__card'));
    const selects = filterForm
      ? Array.from(filterForm.querySelectorAll('select[data-topstripv2-filter]'))
      : [];

    const normalize = (value) => (value || '').toString().trim().toLowerCase();
    const readFilters = () =>
      selects.map((select) => ({
        key: select.dataset.topstripv2Filter,
        value: normalize(select.value),
      }));

    const cardMatchesFilters = (card, filters) => {
      for (let i = 0; i < filters.length; i += 1) {
        const filter = filters[i];
        if (!filter.value || filter.value === 'any') continue;
        const cardValue = normalize(card.getAttribute(`data-${filter.key}`));
        if (!cardValue || !cardValue.includes(filter.value)) {
          return false;
        }
      }
      return true;
    };

    const renderCards = (filters) => {
      let shown = 0;
      cards.forEach((card) => {
        const matches = cardMatchesFilters(card, filters);
        card.style.display = matches ? '' : 'none';
        if (matches) shown += 1;
      });
      return shown;
    };

    const setEmptyState = (shown) => {
      if (emptyState) {
        emptyState.classList.toggle('is-visible', shown === 0);
      }
    };

    const initOptionBaseLabels = () => {
      selects.forEach((select) => {
        Array.from(select.options).forEach((option) => {
          if (!option.dataset.baseLabel) {
            option.dataset.baseLabel = option.textContent.trim();
          }
        });
      });
    };

    const countForOption = (targetSelect, optionValue) => {
      const filters = selects.map((select) => ({
        key: select.dataset.topstripv2Filter,
        value: normalize(select === targetSelect ? optionValue : select.value),
      }));

      let count = 0;
      cards.forEach((card) => {
        if (cardMatchesFilters(card, filters)) count += 1;
      });
      return count;
    };

    const updateOptionCounts = () => {
      if (!selects.length) return;
      initOptionBaseLabels();

      selects.forEach((select) => {
        Array.from(select.options).forEach((option) => {
          const baseLabel = option.dataset.baseLabel || option.textContent.trim();
          const count = countForOption(select, option.value);
          option.textContent = `${baseLabel} (${count})`;
        });
      });
    };

    const applyFilters = (changedSelect = null) => {
      if (!selects.length) return;

      let filters = readFilters();
      let shown = renderCards(filters);

      // If a newly selected value would create a blank state, clear the other filters
      // and keep the newest selection so the customer still sees results.
      if (shown === 0 && changedSelect) {
        const changedValue = normalize(changedSelect.value);
        if (changedValue && changedValue !== 'any') {
          selects.forEach((select) => {
            if (select !== changedSelect) {
              select.value = 'any';
            }
          });
          filters = readFilters();
          shown = renderCards(filters);
        }
      }

      setEmptyState(shown);
      updateOptionCounts();
    };

    selects.forEach((select) => {
      select.addEventListener('change', () => applyFilters(select));
      select.addEventListener('focus', updateOptionCounts);
      select.addEventListener('mousedown', updateOptionCounts);
    });

    if (clearBtn) {
      clearBtn.addEventListener('click', () => {
        selects.forEach((select) => {
          select.value = 'any';
        });
        applyFilters();
      });
    }

    if (filterToggle && filterForm) {
      filterToggle.addEventListener('click', () => {
        const isOpen = filterForm.classList.contains('is-open');
        filterForm.classList.toggle('is-open', !isOpen);
        filterToggle.setAttribute('aria-expanded', isOpen ? 'false' : 'true');
      });
    }

    applyFilters();
  };

  const initAll = () => {
    document.querySelectorAll('.ppv2').forEach((root) => initPpv2(root));
    document.querySelectorAll('.topstripV2').forEach((wrap) => initTopstripV2(wrap));
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    initAll();
  }

  document.addEventListener('product-info:loaded', (event) => {
    const root = event.target.querySelector('.ppv2');
    if (root) initPpv2(root);
    const topstrip = event.target.querySelector('.topstripV2');
    if (topstrip) initTopstripV2(topstrip);
  });
})();
